import { useState, useEffect, useRef } from "react";
import { fetchSentimentData } from "services/rapidcanvas";
import { formatNumber } from "helpers/getNumberFormats";
import { cleanNumber } from "helpers/cleanNumbers";

export const useSentiment = ({ account, products, inputValues, handleNewValues }) => {
  const { localPhone } = account || {};
  const { product, quantity } = inputValues;
  const inputValuesRef = useRef(inputValues);
  inputValuesRef.current = inputValues;

  const [sentimentData, setSentimentData] = useState(null);
  const [isSuggestedQuantity, setIsSuggestedQuantity] = useState(false);
  const [isMatchingProduct, setIsMatchingProduct] = useState(false);
  const [isMatchingSuggestedQuantity, setIsMatchingSuggestedQuantity] = useState(false);

  // Fetch sentiment data on phone change
  useEffect(() => {
    if (localPhone && account?.id) {
      fetchSentimentData(localPhone, account.id)
        .then((data) => setSentimentData(data))
        .catch((err) => console.error("Error fetching sentiment data:", err));
    }
  }, [localPhone, account?.id]);

  // Process sentiment data when received
  useEffect(() => {
    if (!sentimentData?.quantity || products.length === 0 || isSuggestedQuantity) return;

    const suggestedProduct = sentimentData?.product_name
      ? products.find(
          (p) => p.product?.toLowerCase() === sentimentData.product_name?.toLowerCase(),
        )
      : null;

    const productToUse =
      suggestedProduct || products.find((p) => p.id === product) || products[0];
    const matchesProduct =
      productToUse?.product?.toLowerCase() === sentimentData?.product_name?.toLowerCase();

    const newQuantity = formatNumber(sentimentData.quantity);

    const timeoutId = setTimeout(() => {
      const calculatedPrice = productToUse
        ? (parseFloat(newQuantity) * productToUse.price * productToUse.capacity).toFixed(2)
        : "";

      handleNewValues({
        ...inputValuesRef.current,
        product: productToUse.value,
        quantity: newQuantity,
        price: formatNumber(calculatedPrice),
      });
    }, 100);

    setIsSuggestedQuantity(true);
    setIsMatchingProduct(matchesProduct);
    setIsMatchingSuggestedQuantity(true);

    return () => clearTimeout(timeoutId);
  }, [sentimentData, products, isSuggestedQuantity]);

  // Monitor quantity changes
  useEffect(() => {
    if (isSuggestedQuantity && sentimentData?.quantity) {
      const currentQuantity = cleanNumber(quantity);
      setIsMatchingSuggestedQuantity(currentQuantity === sentimentData.quantity);
    }
  }, [quantity, sentimentData, isSuggestedQuantity]);

  const resetSentiment = () => {
    setIsSuggestedQuantity(false);
    setIsMatchingProduct(false);
    setIsMatchingSuggestedQuantity(false);
  };

  return {
    sentimentData,
    isSuggestedQuantity,
    isMatchingProduct,
    isMatchingSuggestedQuantity,
    setIsSuggestedQuantity,
    setIsMatchingProduct,
    resetSentiment,
  };
};
